import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FEATURE_LABELS, LIMIT_LABELS, type ClientEntitlementsView, type PackageSummaryView } from '@lp/contracts';
import { useState } from 'react';
import { Link } from 'react-router';
import { errorText, get, put } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { num } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { FeatureLine, LimitValue, ProductsAllowed } from './package-config';
import { Badge, Button, Card, ErrorBox, Loading, Select, cn } from './ui';

/** a client's package and what it gives, as the server enforces it — with what is used of each limit */
export function ClientPackage({ clientId }: { clientId: number }) {
  const t = useT();
  const qc = useQueryClient();
  const superAdmin = useIsSuperAdmin();
  const e = useQuery({ queryKey: ['client', clientId, 'entitlements'], queryFn: () => get<ClientEntitlementsView>(`/platform/clients/${clientId}/entitlements`) });
  const packages = useQuery({ queryKey: ['packages'], queryFn: () => get<PackageSummaryView[]>('/platform/packages'), enabled: superAdmin });
  const [pick, setPick] = useState('');
  const give = useMutation({
    mutationFn: (x: { packageCode: string; version?: number }) => put<ClientEntitlementsView>(`/platform/clients/${clientId}/package`, x),
    onSuccess: (v) => {
      qc.setQueryData(['client', clientId, 'entitlements'], v);
      setPick('');
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['packages'] });
    },
  });
  const current = e.data?.package ?? null;
  const offered = (packages.data ?? []).filter((p) => p.status === 'active' && p.publishedVersion && p.code !== current?.code);

  return (
    <Card
      title={t('Package')}
      className="mt-4"
      actions={
        current && (
          <Link to={`/packages/${current.code}`} className="text-xs font-medium text-brand-700 hover:underline">
            {t('Open the package')}
          </Link>
        )
      }
    >
      {e.isLoading ? (
        <Loading />
      ) : e.error || !e.data ? (
        <ErrorBox text={errorText(e.error)} />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="client-package">
            {current ? (
              <>
                <span className="text-base font-semibold text-ink-900">{current.nameEn}</span>
                <span className="text-sm text-ink-500">{current.nameKm}</span>
                <Badge>v{current.version}</Badge>
                {current.status === 'archived' && <Badge>{t('Archived')}</Badge>}
                {current.newerVersion && (
                  <Badge tone="blue">
                    {t('Newer')}: v{current.newerVersion}
                  </Badge>
                )}
              </>
            ) : (
              <Badge tone="red">{t('No package — the client may not have or sell anything')}</Badge>
            )}
          </div>
          {e.data.over.length > 0 && <ErrorBox text={`${t('More than the package allows')}: ${e.data.over.join('; ')}`} />}

          {current && (
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Products')}</h3>
                <ProductsAllowed products={e.data.products} />
              </div>
              <div>
                <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Used of the limits')}</h3>
                <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm">
                  {e.data.limits.map((l) => (
                    <div key={l.key} className="contents">
                      <dt className="text-ink-600">{t(LIMIT_LABELS[l.key])}</dt>
                      <dd className={cn('text-right tabular-nums', l.limit != null && l.used >= l.limit && 'font-semibold text-amber-700')} data-testid={`limit-${l.key}`}>
                        {num(l.used)} / <LimitValue limit={l.limit} />
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div>
                <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Features')}</h3>
                <ul>
                  {e.data.features.map((f) => (
                    <FeatureLine key={f.key} on={f.on} label={t(FEATURE_LABELS[f.key])} />
                  ))}
                </ul>
              </div>
            </div>
          )}

          {superAdmin && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ink-100 pt-3">
              {current?.newerVersion && (
                <Button variant="secondary" onClick={() => give.mutate({ packageCode: current.code, version: current.newerVersion! })} disabled={give.isPending}>
                  {t('Move to')} v{current.newerVersion}
                </Button>
              )}
              {offered.length > 0 && (
                <>
                  <Select value={pick} onChange={(ev) => setPick(ev.target.value)} aria-label={t('Change package')}>
                    <option value="">{t(current ? 'Change package' : 'Give a package')}</option>
                    {offered.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.nameEn} ({p.code}) v{p.publishedVersion}
                      </option>
                    ))}
                  </Select>
                  <Button onClick={() => pick && give.mutate({ packageCode: pick })} disabled={!pick || give.isPending}>
                    {t(current ? 'Change' : 'Give')}
                  </Button>
                </>
              )}
            </div>
          )}
          {give.error && <ErrorBox text={errorText(give.error)} />}
        </>
      )}
    </Card>
  );
}
